import React, { useCallback, useRef, type JSX } from 'react';
import Button from '@resources/js/components/Button/Button';
import styles from './Fullscreenable.module.scss';
import Advertisements from '../Advertisements/Advertisements';
import { ArchonAdPlacement } from '../ArchonAdPlacement/ArchonAdPlacement';
import { ArchonAdPlacementWithCloseButton } from '../ArchonAdPlacementWithCloseButton/ArchonAdPlacementWithCloseButton';
import useShowAdContainers from '../../hooks/useShowAdContainers';
import cx from 'classnames';
import { useIsFullscreen } from '@resources/js/hooks/useIsFullscreen';

type FullscreenableProps = {
  children?: React.ReactNode;
  shouldShowAdsIfEnabled?: boolean;
};

export default function Fullscreenable({
  children,
  shouldShowAdsIfEnabled = true,
}: FullscreenableProps): JSX.Element {
  const { isFullscreen, fullscreenElement } = useIsFullscreen();
  const ref = useRef<HTMLDivElement | null>(null);

  const enterFullscreen = useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }
    if (isFullscreen) {
      return;
    }

    ref.current?.requestFullscreen().catch(console.error);
  }, [isFullscreen]);

  const exitFullscreen = useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }
    if (!isFullscreen) {
      return;
    }

    document.exitFullscreen().catch(console.error);
  }, [isFullscreen]);

  const enterOrExitFullscreen = useCallback(() => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  }, [isFullscreen, enterFullscreen, exitFullscreen]);

  const showAdContainers = useShowAdContainers();
  const showAds =
    fullscreenElement !== null &&
    // eslint-disable-next-line react-hooks/refs
    fullscreenElement === ref.current &&
    shouldShowAdsIfEnabled &&
    showAdContainers;

  return (
    <div
      ref={ref}
      className={cx({
        [styles.container]: true,
        [styles.containerWithFullscreen]: isFullscreen,
      })}
    >
      <div
        className={cx({
          [styles.content]: true,
          [styles.contentWithAds]: showAds,
          [styles.contentWithFullscreen]: isFullscreen,
        })}
      >
        {children}
        {showAds && (
          <div className={styles.contentSidebar}>
            <Advertisements className={styles.contentSidebarSticky}>
              <ArchonAdPlacement location={'sideRailWide'} />
            </Advertisements>
          </div>
        )}
      </div>
      <Button
        styles={['menu', 'slim']}
        className={cx({
          [styles.button]: true,
          [styles.buttonWithAds]: showAds,
        })}
        onClick={enterOrExitFullscreen}
      >
        {isFullscreen
          ? trans('global.exit_fullscreen')
          : trans('global.fullscreen')}
      </Button>
      {showAds && (
        <span className={styles.stickyFooterAd}>
          <ArchonAdPlacementWithCloseButton>
            <ArchonAdPlacement location={'stickyFooter'} />
          </ArchonAdPlacementWithCloseButton>
        </span>
      )}
    </div>
  );
}
